import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Alert, Button, IconButton } from '@socialchorus/shared-ui-components';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { contentSelectors } from '../../../models/content';
import useFeatureFlag from '../../../common/use-feature-flag';
import { Feature } from '../../../models/features/features';
import './ugc-preview-banner.scss';

interface UgcPreviewBannerProps {
  contentId: number;
  isUgcPreview?: boolean;
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

export const UgcPreviewBanner: React.FC<UgcPreviewBannerProps> = ({
  contentId,
  isUgcPreview = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [dismissed, setDismissed] = useState(false);

  const isUgcV2 = usePatronSelector((state) =>
    contentSelectors.getContentIsUgcV2(state, { contentId })
  );
  const isDraft = usePatronSelector((state) =>
    contentSelectors.getContentIsDraft(state, { contentId })
  );
  const isPending = usePatronSelector((state) =>
    contentSelectors.getContentIsPending(state, { contentId })
  );
  const updatedAt = usePatronSelector((state) =>
    contentSelectors.getContentUpdatedAt(state, { contentId })
  );
  const ugcEnabled = useFeatureFlag(Feature.UGC_ENABLED);

  const isUgc = ugcEnabled && (isUgcPreview || isUgcV2);

  if (!isUgc || (!isDraft && !isPending) || dismissed) {
    return null;
  }

  const handleContinueEditing = () => {
    history.push(`/post/${contentId}`);
  };

  const formattedDate = formatDate(updatedAt);

  const config = isDraft
    ? {
        type: 'info' as const,
        iconName: 'emoji_objects',
        title: t('user_generated_content.banner_draft', 'This is a draft post'),
        description: t(
          'user_generated_content.banner_draft_description',
          'This is a private draft, created on {{date}} and only visible to you.',
          { date: formattedDate }
        ),
      }
    : {
        type: 'info' as const,
        iconName: 'emoji_objects',
        title: t(
          'user_generated_content.banner_pending',
          'This post is pending review'
        ),
        description: t(
          'user_generated_content.banner_pending_description',
          'This post was submitted for review on {{date}} and is only visible to you until approved.',
          { date: formattedDate }
        ),
      };

  const actionButtons = (
    <div className="ugc-preview-banner__actions">
      {isDraft ? (
        <Button
          variant="text"
          size="compact"
          label={t(
            'user_generated_content.continue_editing',
            'Continue Editing'
          )}
          onClick={handleContinueEditing}
        />
      ) : null}
      <IconButton
        aria-label={t('common.close', 'Close')}
        iconName="close"
        onClick={() => setDismissed(true)}
      />
    </div>
  );

  return (
    <div className="ugc-preview-banner">
      <Alert
        type={config.type}
        iconName={config.iconName}
        title={config.title}
        description={config.description}
        size="default"
        actionButton={actionButtons}
      />
    </div>
  );
};
